/**
 * Tag filter chips for filtering images by tags.
 */
import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/solid';
import type { MediaTag } from '@/types/media';

interface TagFilterProps {
  tags: MediaTag[];
  selectedTagIds: string[];
  onChange: (tagIds: string[]) => void;
  isLoading?: boolean;
}

export function TagFilter({ tags, selectedTagIds, onChange, isLoading }: TagFilterProps) {
  const toggleTag = (tagId: string) => {
    if (selectedTagIds.includes(tagId)) {
      onChange(selectedTagIds.filter((id) => id !== tagId));
    } else {
      onChange([...selectedTagIds, tagId]);
    }
  };

  const clearAll = () => {
    onChange([]);
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-6 w-16 bg-gray-100 rounded-full animate-pulse" />
        ))}
      </div>
    );
  }

  // Only show tags that have images
  const tagsWithImages = tags.filter((tag) => tag.image_count > 0);

  if (tagsWithImages.length === 0) {
    return null;
  }

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-sm text-gray-500">Tags:</span>
      {tagsWithImages.map((tag) => {
        const isSelected = selectedTagIds.includes(tag.id);
        return (
          <button
            key={tag.id}
            onClick={() => toggleTag(tag.id)}
            className={`
              inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium
              transition-all cursor-pointer
              ${isSelected
                ? 'ring-2 ring-offset-1'
                : 'opacity-70 hover:opacity-100'
              }
            `}
            style={{
              backgroundColor: `${tag.color}20`,
              color: tag.color,
              ...(isSelected ? { ringColor: tag.color } : {}),
            }}
          >
            {tag.name}
            {tag.image_count > 0 && (
              <span className="ml-1 opacity-70">({tag.image_count})</span>
            )}
          </button>
        );
      })}
      {selectedTagIds.length > 0 && (
        <button
          onClick={clearAll}
          className="text-xs text-gray-500 hover:text-gray-700 flex items-center"
        >
          <XMarkIcon className="w-3 h-3 mr-0.5" />
          Clear
        </button>
      )}
    </div>
  );
}
